import React, { useEffect, useState } from 'react';
import {
  Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack, TextField,
} from '@mui/material';
import { Receipt, OnAccountReceiptCreateDTO, ReceiptMethod } from '../../interfaces/sales/receipt.interface';
import { ReceiptService } from '../../services/receipt.service';
import { AccountService } from '../../services/account.service';
import { Account } from '../../interfaces/accounting.interface';
import { AccountAutocomplete } from '../common/AccountAutocomplete';
import { useToast } from '../../context/ToastContext';
import { defaultReceiptMethodForAccount, depositAccountOptions, foreignCurrencyNameHint } from '../../utils/postingAccounts';
import { toLocalIsoDate } from '../../utils/dateUtils';

const RECEIPT_METHODS: { value: ReceiptMethod; label: string }[] = [
  { value: 'CASH', label: 'Cash' },
  { value: 'BANK_TRANSFER', label: 'Bank Transfer' },
  { value: 'CHEQUE', label: 'Cheque' },
  { value: 'MOBILE_BANKING', label: 'Mobile Banking' },
  { value: 'OTHER', label: 'Other' },
];

interface Props {
  open: boolean;
  customerId: string;
  customerName?: string;
  onClose: () => void;
  onPosted: (r: Receipt) => void;
}

const RecordOnAccountReceiptDialog: React.FC<Props> = ({ open, customerId, customerName, onClose, onPosted }) => {
  const toast = useToast();
  const [assetAccounts, setAssetAccounts] = useState<Account[]>([]);
  const [amount, setAmount] = useState<number>(NaN);
  const [receiptDate, setReceiptDate] = useState(toLocalIsoDate());
  const [depositAccountId, setDepositAccountId] = useState('');
  const [method, setMethod] = useState<ReceiptMethod>('CASH');
  const [methodTouched, setMethodTouched] = useState(false);
  const [reference, setReference] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    AccountService.getAccountsByType('ASSET')
      .then((accounts: Account[]) => setAssetAccounts(accounts))
      .catch((e: unknown) => toast.showApiError(e, 'Failed to load accounts'));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (open) {
      setAmount(NaN);
      setReceiptDate(toLocalIsoDate());
      setDepositAccountId('');
      setMethod('CASH');
      setMethodTouched(false);
      setReference('');
    }
  }, [open]);

  const amountValid = Number.isFinite(amount) && amount > 0;
  const submitDisabled = saving || !amountValid || !depositAccountId || !receiptDate;

  const handleSubmit = async () => {
    if (submitDisabled) return;
    setSaving(true);
    try {
      const dto: OnAccountReceiptCreateDTO = {
        customerId,
        amount,
        receiptDate,
        method,
        reference: reference.trim() || undefined,
        depositAccountId,
      };
      const receipt = await ReceiptService.createOnAccount(dto);
      toast.showSuccess('Payment received on account');
      onPosted(receipt);
      onClose();
    } catch (e) {
      toast.showApiError(e, 'Failed to record payment');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="sm" fullWidth data-help-page="sales.onAccountReceipt.page">
      <DialogTitle>Receive payment{customerName ? ` - ${customerName}` : ''}</DialogTitle>
      <DialogContent data-help-section="sales.onAccountReceipt.section.form">
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            data-help="sales.onAccountReceipt.amount"
            label="Amount"
            type="number"
            value={Number.isFinite(amount) ? amount : ''}
            onChange={(e) => setAmount(e.target.value === '' ? NaN : Number(e.target.value))}
            inputProps={{ min: 0, step: 'any' }}
            fullWidth
            required
            error={Number.isFinite(amount) && !amountValid}
            helperText={Number.isFinite(amount) && !amountValid ? 'Amount must be greater than 0' : undefined}
          />
          <TextField
            data-help="sales.onAccountReceipt.date"
            label="Receipt Date"
            type="date"
            value={receiptDate}
            onChange={(e) => setReceiptDate(e.target.value)}
            fullWidth
            required
            InputLabelProps={{ shrink: true }}
          />
          <AccountAutocomplete
            data-help="sales.onAccountReceipt.depositAccount"
            accounts={depositAccountOptions(assetAccounts)}
            value={depositAccountId}
            onChange={(v: string) => {
              setDepositAccountId(v);
              if (!methodTouched) setMethod(defaultReceiptMethodForAccount(assetAccounts.find(a => a.id === v)));
            }}
            label="Deposit Account"
            required
            helperText={foreignCurrencyNameHint(assetAccounts.find(a => a.id === depositAccountId)) ?? undefined}
            error={!!foreignCurrencyNameHint(assetAccounts.find(a => a.id === depositAccountId))}
          />
          <TextField
            data-help="sales.onAccountReceipt.method"
            select
            label="Method"
            value={method}
            onChange={(e) => { setMethodTouched(true); setMethod(e.target.value as ReceiptMethod); }}
            fullWidth
            required
          >
            {RECEIPT_METHODS.map((m) => (
              <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
            ))}
          </TextField>
          <TextField
            data-help="sales.onAccountReceipt.reference"
            label="Reference (optional)"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button data-help="sales.onAccountReceipt.cancel" onClick={onClose} disabled={saving}>Cancel</Button>
        <Button data-help="sales.onAccountReceipt.submit" onClick={handleSubmit} variant="contained" disabled={submitDisabled}>
          {saving ? 'Saving…' : 'Record payment'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RecordOnAccountReceiptDialog;
